import { UserType, UserJson, UserOrg, IDigitalDataEvent, IUserTailoring, IUserBaseEvent, INativeUserTailoring, IUserBrandConfig } from '@nab/x-types';

/**
 * @class BaseUser
 * Base user class that will be used by the web, mobile or server shell to instantiate user data and actions, which will be used by the mini-apps.
 * The Base user takes in the users data retrieved from respective down stream system and build the user object
 * @property id: Unique User identifier
 * @property idHash: Hashed user identifier
 * @property type: User type either 'customer' | 'banker'. For anonymous access we would still consider them as customer with no personal data.
 * @property org: Organization identifier, helps identify the partner or NAB context. Values : NAB | BNZ | UBank
 * @property authenticated: Boolean value to indicate if the user is authenticated or not.
 * @property sessionId: Unique session identifier, helps with data security, logging correlation, etc...
 *
 * @function emitEvent: used to emit user action events. The data sent via the events are used to trigger the services accordingly.
 * The default behaviour is currently tied with support to the Digital Data layer that is used for Analytics and Marketing.
 * The use will be expanded further based on the features
 */
abstract class BaseUser {
    public readonly id: string;

    public readonly idHash: string;

    public readonly type: UserType;

    public readonly org: UserOrg;

    public readonly details?: { [key: string]: any };

    public context?: { [key: string]: any } = {};

    public abstract readonly tailoring?: IUserTailoring | INativeUserTailoring;

    public authenticated: boolean;

    public sessionId: string;

    public readonly brand?: IUserBrandConfig;

    constructor({ id, idHash, type, org, authenticated, sessionId, details, context, brand }: UserJson) {
        this.id = id;
        this.idHash = idHash;
        this.type = type;
        this.org = org;
        this.authenticated = authenticated;
        this.sessionId = sessionId;
        this.details = details;
        this.context = context;
        this.brand = brand;
    }

    toJSON(): UserJson {
        const { id, org, type, authenticated, sessionId, idHash, details, context, brand } = this;
        return {
            id,
            org,
            idHash,
            type,
            authenticated,
            sessionId,
            details: { ...details },
            context: { ...context },
            brand
        };
    }

    public abstract emitEvent<T extends IUserBaseEvent = IDigitalDataEvent>(payload: T): Promise<boolean>;
}

export { BaseUser };
